import { Menu, Transition } from "@headlessui/react";
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FC, Fragment, SetStateAction } from "react";
import { tabs as _tabs, classNames } from "utils";
import { setActiveBroker, setSelectedOffer } from "features/offers";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useTranslation } from "react-i18next";

import { BrokerTypes } from "graphql/__generated__/globalTypes";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { setSelectedTreaty } from "features/treaty";

export type iTab = {
    value: any;
    label: string;
};

type TabsProps = {
    setYear: React.Dispatch<SetStateAction<string>>;
    setActiveTab?: React.Dispatch<SetStateAction<any>>;
    year: string;
    activeTab?: string;
    tabs?: any[];
    renderAction?: React.ReactNode;
};

const Tabs: FC<TabsProps> = ({
    setYear,
    year,
    setActiveTab,
    tabs = _tabs,
    renderAction,
}) => {
    const { t } = useTranslation();
    const { broker } = useAppSelector((state) => state.offers);
    const dispatch = useAppDispatch();

    const hanleBrokerSelect = (tab: BrokerTypes) => {
        setActiveTab?.(tab);
        dispatch(setActiveBroker(tab));
        dispatch(setSelectedOffer(null));
        dispatch(setSelectedTreaty(null));
    };

    // Generate years array from 2019 to current year
    const yearOptions = Array.from(
        { length: new Date().getFullYear() - 2019 + 1 },
        (_, i) => {
            const yearValue = 2019 + i;
            return {
                name: yearValue.toString(),
                value: yearValue.toString(),
            };
        },
    );

    return (
        <div className="mt-3 sm:mt-2 flex-1">
            <div className="sm:hidden">
                <label htmlFor="tabs" className="sr-only">
                    {t("navigation.tabs.select_tab")}
                </label>
                <select
                    id="tabs"
                    name="tabs"
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md"
                    defaultValue={0}
                    onChange={(e) =>
                        hanleBrokerSelect(e.target.value as BrokerTypes)
                    }
                    value={broker as BrokerTypes}>
                    {tabs.map((el, key) => (
                        <option key={key} value={el.href}>
                            {el.name}
                        </option>
                    ))}
                </select>
            </div>
            <div className="hidden sm:block">
                <div className="flex items-center border-b border-gray-200">
                    <nav
                        className="flex-1 -mb-px flex space-x-6 xl:space-x-8"
                        aria-label={t("navigation.tabs.tabs_label")}>
                        {tabs.map((tab) => (
                            <button
                                key={tab.name}
                                disabled={tab.disabled}
                                onClick={() => {
                                    if (!tab.disabled) {
                                        hanleBrokerSelect(tab.href);
                                    }
                                }}
                                className={classNames(
                                    tab.href === broker
                                        ? "border-green-500 text-green-600"
                                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                                    "whitespace-nowrap disabled:text-gray-400 disabled:cursor-not-allowed py-4 px-1 border-b-2 font-medium cursor-pointer text-sm",
                                )}>
                                {tab.name}
                            </button>
                        ))}
                    </nav>

                    <div className="space-x-3 flex items-center">
                        {renderAction}
                        <div className="relative inline-flex rounded-md shadow-sm">
                            <button
                                type="button"
                                className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500">
                                {t("navigation.tabs.select_year", { year })}
                            </button>
                            <Menu as="div" className="relative -ml-px block">
                                <Menu.Button className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500">
                                    <span className="sr-only">
                                        {t("navigation.tabs.open_options")}
                                    </span>
                                    <ChevronDownIcon
                                        className="h-6 w-6"
                                        aria-hidden="true"
                                    />
                                </Menu.Button>
                                <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform opacity-0 scale-95"
                                    enterTo="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 scale-100"
                                    leaveTo="transform opacity-0 scale-95">
                                    <Menu.Items className="absolute z-50 right-0 mt-2 -mr-1 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                        <div className="py-1">
                                            {yearOptions.map((yearOption) => (
                                                <Menu.Item
                                                    key={yearOption.name}>
                                                    {({ active }) => (
                                                        <span
                                                            onClick={() =>
                                                                setYear(
                                                                    yearOption.value,
                                                                )
                                                            }
                                                            className={classNames(
                                                                active
                                                                    ? "bg-gray-100 text-gray-900"
                                                                    : "text-gray-700",
                                                                "block px-4 py-2 text-sm cursor-pointer",
                                                            )}>
                                                            {yearOption.name}
                                                        </span>
                                                    )}
                                                </Menu.Item>
                                            ))}
                                        </div>
                                    </Menu.Items>
                                </Transition>
                            </Menu>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Tabs;
